import {
	auditService,
	casesLawyerService,
	GET_LAWYER_CASE_PHASES,
} from '@/api';
import AppRoutes from '@/const/routes';
import { PhaseName } from '@/interfaces';
import { useQuery } from '@tanstack/react-query';
import { useRouter } from 'next/router';
import React from 'react';
import { useCaseStep } from './useCaseStep';
import { getValidCaseId } from '@/utils';

export const useCasePhases = () => {
	const router = useRouter();
	const caseId = getValidCaseId(router.query.id);
	const id = caseId;

	const { isPhaseCompleted, isPhaseCompletedById } = useCaseStep();

	const baseUrl = `${AppRoutes.LAWYERAPP}/cases/${id}`;
	const { data: casePhases } = useQuery({
		queryKey: [GET_LAWYER_CASE_PHASES, caseId],
		queryFn: () => casesLawyerService.getCasePhases(),
	});

	const casePhasesData = React.useMemo(() => {
		if (!casePhases?.data) return [];
		const phases = casePhases?.data.map((d) => ({
			...d,
			href: `${baseUrl}/${d.name.replace(' ', '-').toLowerCase()}`,
		}));

		return [
			...phases,
			{
				id: 100,
				name: 'Case closed' as PhaseName,
				href: '#',
			},
		];
	}, [casePhases]);

	const isCurrentPhase = (phase: PhaseName) => {
		if (!id && phase === 'Investigation') {
			return true;
		}

		if (casePhasesData && casePhasesData.length > 0) {
			const index = casePhasesData.findIndex((p) => p.name === phase);

			if (index === -1) return false;

			if (index === 0) {
				return !isPhaseCompleted(phase);
			}

			const prevPhase = casePhasesData[index - 1];

			if (!prevPhase) return false;

			return isPhaseCompleted(prevPhase.name) && !isPhaseCompleted(phase);
		}

		return false;
	};

	const getCurrentPhase = React.useCallback(
		async (
			currentId: string
		): Promise<{ href: string; name: string } | null> => {
			const validCurrentId = getValidCaseId(currentId);
			if (!validCurrentId || !casePhasesData || casePhasesData.length === 0)
				return null;
			const { data: steps } = await auditService.getStepStatus(validCurrentId);

			for (let i = 0; i < casePhasesData.length; i++) {
				const phase = casePhasesData[i];

				if (i === 0) {
					if (
						!(await isPhaseCompletedById(steps, phase.name, validCurrentId))
					) {
						return {
							...phase,
							href: `${AppRoutes.LAWYERAPP}/cases/${validCurrentId}/${phase.name
								.replace(' ', '-')
								.toLowerCase()}`,
						};
					}
				} else {
					const prevPhase = casePhasesData[i - 1];
					const prevCompleted = await isPhaseCompletedById(
						steps,
						prevPhase.name,
						validCurrentId
					);
					const currentCompleted = await isPhaseCompletedById(
						steps,
						phase.name,
						validCurrentId
					);

					if (prevCompleted && !currentCompleted) {
						return {
							...phase,
							href: `${AppRoutes.LAWYERAPP}/cases/${validCurrentId}/${phase.name
								.replace(' ', '-')
								.toLowerCase()}`,
						};
					}
				}
			}

			return casePhasesData[casePhasesData.length - 1];
		},
		[casePhasesData, isPhaseCompletedById]
	);

	return {
		isCurrentPhase,
		casePhasesData,
		getCurrentPhase,
	};
};
